import { isNotNullish, isNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { alertDialog, confirmDialog, guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing, type TemplateResult } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { changesContainPaths } from '../../api/changesContainPaths.js';
import { ContractAPI } from '../../api/ContractAPI.js';
import type { AppliedContract } from '../../entities/AppliedContract.js';
import type { ContractSupplement, ContractSupplementChange } from '../../entities/ContractSupplement.js';
import { ChangeList } from '../components/changes/ChangeEntry.js';

export interface SupplementCreationDialogResult {
  supplement: null | ContractSupplement;
  comment: string;
}

interface SupplementCreationDialogProps {
  contract: Maybe<AppliedContract>;
  supplement: Maybe<ContractSupplement>;
  supplementFromChange: Maybe<ContractSupplement>;
  allSupplements: ContractSupplement[];
  simpleMode?: boolean;
  kuendigungSetDate?: Maybe<Date>;
}

type SaveType = 'save_new_supplement' | 'save_supplement_sub' | 'save_supplement' | 'save_base_contract';

type SupplementDateKey =
  'startDateGeneral' | 'endDateGeneral' | 'startDateAG1' | 'endDateAG1' | 'startDateAG2' | 'endDateAG2';

const getAccessibleTabs = (props: SupplementCreationDialogProps): Array<{ id: SaveType; label: string }> => {
  if (props.simpleMode) return [];
  const tabs: Array<{ id: SaveType; label: string }> = [];
  if (props.contract?._id && props.supplement?._id) tabs.push({ id: 'save_supplement', label: 'Nachtrag speichern' });

  if (props.contract?._id && props.supplement?._id && props.supplementFromChange?.changes?.length)
    tabs.push({ id: 'save_supplement_sub', label: 'Subnachtrag erstellen' });

  if (props.contract?._id && props.supplementFromChange?.changes?.length && !props.kuendigungSetDate)
    tabs.push({ id: 'save_new_supplement', label: 'Neuen Nachtrag erstellen' });

  if (props.supplementFromChange?.changes?.length) tabs.push({ id: 'save_base_contract', label: 'Vertrag ändern' });

  return tabs;
};

const RenderChanges = (changes: Maybe<Array<ContractSupplementChange>>) => {
  if (!changes) return html`<p class="text-base-content/70">Keine Änderungen vorhanden</p>`;

  return html`
    <div class="flex flex-col gap-2">
      <h3 class="font-semibold">Zu übernehmende Änderungen</h3>
      ${ChangeList({
        changes,
        emptyState: html`<p class="text-base-content/70">Keine Änderungen vorhanden</p>`,
      })}
    </div>
  `;
};

const SupplementRuntime = (supplement: Maybe<ContractSupplement>) => {
  if (!supplement) return nothing;
  const activeCategories: TemplateResult[] = [];

  const template = (label: string, start: SupplementDateKey, end: SupplementDateKey) => html`
    <div class="flex flex-col gap-1">
      <span class="text-sm font-semibold text-base-content/70">${label}</span>
      <div class="flex flex-row items-center gap-1">
        ${DateInput({
          clearable: true,
          placeholder: 'Start',
          placeholderMode: 'hide',
          value: supplement[start] ?? undefined,
          onPicked: (date: Date | null) => {
            supplement[start] = date;
          },
        })}
        <span class="font-semibold">-</span>
        ${DateInput({
          clearable: true,
          placeholder: 'Offenes Ende',
          placeholderMode: 'hide',
          value: supplement[end] ?? undefined,
          onPicked: (date: Date | null) => {
            supplement[end] = date;
          },
        })}
      </div>
    </div>
  `;

  void changesContainPaths(supplement.changes, {
    generalCallback: () => {
      activeCategories.push(template('Laufzeit für allgemeine Informationen', 'startDateGeneral', 'endDateGeneral'));
    },
    ag1Callback: () => {
      activeCategories.push(template('Laufzeit für Eingruppierung 1', 'startDateAG1', 'endDateAG1'));
    },
    ag2Callback: () => {
      activeCategories.push(template('Laufzeit für Eingruppierung 2', 'startDateAG2', 'endDateAG2'));
    },
  });

  return html`<div class="flex flex-col gap-2">${activeCategories}</div>`;
};

const SupplementCreationDialogContent = stateful(
  (props: SupplementCreationDialogProps & { resolve: (result: SupplementCreationDialogResult) => void }) => {
    const tabs = getAccessibleTabs(props);
    const [saveType, setSaveType] = useState<SaveType | null>(tabs[0]?.id ?? null);
    const [justification, setJustification] = useState('');
    const [comment, setComment] = useState('');
    const [supplementCopy, setSupplementCopy] = useState<Maybe<Record<string, any>>>(null);

    const handleTabSwitch = (newSaveType: SaveType) => {
      setSaveType(newSaveType);
      if (!props.supplementFromChange) return;
      const supplementFromChange = props.supplementFromChange;

      // if we want to save a sub supplement, we need to set the same timeframe as the base supplement
      if (newSaveType === 'save_supplement_sub' && !supplementCopy) {
        // we copy the supplement to save the old dates before we overwrite them
        setSupplementCopy(JSON.parse(JSON.stringify(supplementFromChange)));

        // get the lowest date of the base supplement
        const startDate = [
          props.supplement?.startDateGeneral,
          props.supplement?.startDateAG1,
          props.supplement?.startDateAG2,
        ]
          .filter(isNotNullish)
          .sort((a, b) => a.getTime() - b.getTime())[0];

        // get the highest date of the base supplement
        const endDate = [props.supplement?.endDateGeneral, props.supplement?.endDateAG1, props.supplement?.endDateAG2]
          .filter(isNotNullish)
          .sort((a, b) => b.getTime() - a.getTime())[0];

        // set the date corresponding date on the new supplement if the path matches
        void changesContainPaths(supplementFromChange.changes, {
          generalCallback: () => {
            supplementFromChange.startDateGeneral = startDate;
            supplementFromChange.endDateGeneral = endDate;
          },
          ag1Callback: () => {
            supplementFromChange.startDateAG1 = startDate;
            supplementFromChange.endDateAG1 = endDate;
          },
          ag2Callback: () => {
            supplementFromChange.startDateAG2 = startDate;
            supplementFromChange.endDateAG2 = endDate;
          },
        });
      } else if (newSaveType !== 'save_supplement_sub' && supplementCopy) {
        // if we switch the tab, we need to reset the dates to the old ones
        supplementFromChange.startDateGeneral = supplementCopy.startDateGeneral
          ? new Date(supplementCopy.startDateGeneral)
          : null;
        supplementFromChange.endDateGeneral = supplementCopy.endDateGeneral
          ? new Date(supplementCopy.endDateGeneral)
          : null;
        supplementFromChange.startDateAG1 = supplementCopy.startDateAG1 ? new Date(supplementCopy.startDateAG1) : null;
        supplementFromChange.endDateAG1 = supplementCopy.endDateAG1 ? new Date(supplementCopy.endDateAG1) : null;
        supplementFromChange.startDateAG2 = supplementCopy.startDateAG2 ? new Date(supplementCopy.startDateAG2) : null;
        supplementFromChange.endDateAG2 = supplementCopy.endDateAG2 ? new Date(supplementCopy.endDateAG2) : null;

        setSupplementCopy(null);
      }
    };

    const getSubSupplementNumber = () => {
      const subNumber = props.supplement?.nachtragsNummer;

      const subsubNumer =
        (props.allSupplements
          ?.filter(s => s.nachtragsNummer === subNumber)
          .sort((a, b) =>
            isNullish(a.subNachtragsnummer) || isNullish(b.subNachtragsnummer)
              ? 1
              : a.subNachtragsnummer - b.subNachtragsnummer,
          )[0]?.subNachtragsnummer ?? 0) + 1;

      return subsubNumer;
    };

    const renderSimpleMode = () => html`
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <span class="text-sm font-semibold text-base-content/70">Begründung für den Nachtrag</span>
          ${TextareaInput({
            placeholder: 'Hier können Sie eine eventuelle Begründung für den Nachtrag eingeben...',
            value: justification,
            onPicked: (value: string | null) => setJustification(value ?? ''),
          })}
        </div>
        <div class="flex flex-col gap-2">
          <span class="text-sm font-semibold text-base-content/70"
            >Wollen Sie der MAV etwas Zusätzliches mitteilen?</span
          >
          ${TextInput({
            placeholder: 'Optionale Informationen',
            placeholderMode: 'hide',
            clearable: true,
            value: comment,
            onPicked: (value: string | null) => setComment(value ?? ''),
          })}
        </div>

        <hr class="border-base-300" />

        <p class="max-w-[600px]">
          ${
            !props.supplement?._id
              ? `Die folgenden Änderungen werden gespeichert und der MAV und Personalverwaltung vorgelegt. `
              : 'Die folgenden Änderungen werden in den vorherigen Antrag übernommen und der MAV vorgelegt.'
          }
        </p>

        <hr class="border-base-300" />

        ${RenderChanges(props.supplementFromChange?.changes)}

        <button
          class="btn btn-primary"
          @click=${async () => {
            if (!props.supplement) return;
            if (
              !(await confirmDialog(`Möchten Sie fortfahren?`, {
                acceptText: 'Fortfahren',
                declineText: 'Abbrechen',
              }))
            )
              return;

            // supplement is new as no _id is given
            if (!props.supplement._id) {
              props.supplement.justification = justification;
              await guardDialog(() => props.supplementFromChange!.create(), {
                success: () => {
                  void SgSnackbar.show('Nachtrag erfolgreich erstellt.');
                  props.resolve({ supplement: props.supplementFromChange ?? null, comment });
                },
              });
            } else if (props.supplement._id && !props.supplementFromChange?._id) {
              // write changes onto the supplement
              for (const change of props.supplementFromChange?.changes ?? []) {
                const existingChange = props.supplement.changes?.find(c => c.path === change.path);
                if (existingChange) {
                  existingChange.value = change.value;
                } else {
                  props.supplement.changes ??= [];
                  props.supplement.changes.push(change);
                }
              }

              // new changes might have been added to the supplement and into paths which do not have a start and and date. In the simple mode we set them to the current values
              const startDate =
                props.supplementFromChange?.startDateGeneral ??
                props.supplement.startDateAG1 ??
                props.supplement.startDateAG2;
              const endDate =
                props.supplementFromChange?.endDateGeneral ??
                props.supplement.endDateAG1 ??
                props.supplement.endDateAG2;

              await changesContainPaths(props.supplement.changes, {
                generalCallback: () => {
                  if (props.supplement && !props.supplement.startDateGeneral)
                    props.supplement.startDateGeneral = startDate;
                  if (props.supplement && !props.supplement.endDateGeneral) props.supplement.endDateGeneral = endDate;
                },
                ag1Callback: () => {
                  if (props.supplement && !props.supplement.startDateAG1) props.supplement.startDateAG1 = startDate;
                  if (props.supplement && !props.supplement.endDateAG1) props.supplement.endDateAG1 = endDate;
                },
                ag2Callback: () => {
                  if (props.supplement && !props.supplement.startDateAG2) props.supplement.startDateAG2 = startDate;
                  if (props.supplement && !props.supplement.endDateAG2) props.supplement.endDateAG2 = endDate;
                },
              });

              await guardDialog(
                async () => {
                  if (!props.contract || !props.supplement) return;
                  props.supplement.justification = justification;
                  await props.supplement.save();
                  await ContractAPI.notifyMAV(props.contract._id, comment, props.supplement._id, true)();
                },
                {
                  success: () => {
                    void SgSnackbar.show('Nachtrag erfolgreich gespeichert.');
                    props.resolve({ supplement: null, comment });
                  },
                },
              );
            }
          }}
        >
          Nachtrag erstellen und der MAV vorlegen
        </button>
      </div>
    `;

    const renderEditNewSupplement = () => html`
      <div class="flex flex-col gap-4">
        <p class="max-w-[600px]">
          Die Änderungen am Vertrag werden in einen neuen Nachtrag festgehalten. Dieser besitzt verschiedene Laufzeiten
          für die unterschiedlichen Informationen, außerdem muss er erst vom Arbeitnehmer unterzeichnet werden um in
          Kraft zu treten. Der Basisvertrag bleibt somit unverändert.
        </p>
        ${SupplementRuntime(props.supplementFromChange)} ${RenderChanges(props.supplementFromChange?.changes)}

        <button
          class="btn btn-primary"
          @click=${async () => {
            if (!props.supplement) return;

            // check if some idiot user has changed the einstellungsdatum in a supplement
            if (props.supplementFromChange?.changes?.find(c => c.path.includes('einstellungsdatum'))) {
              await alertDialog(
                'Sie können das Einstellungsdatum nicht in einem Nachtrag ändern. Bitte benutzen Sie für Änderungen einer Eingruppierung die Start- und Enddatumsfelder des Nachtrages.',
              );
              return;
            }

            if (
              !(await confirmDialog(
                `Möchten Sie wirklich einen neuen Nachtrag mit der Nummer ${props.supplementFromChange?.nachtragsNummer} erstellen?`,
              ))
            )
              return;

            await guardDialog(() => props.supplementFromChange!.create(), {
              success: () => {
                void SgSnackbar.show('Nachtrag erfolgreich erstellt.');
                props.resolve({ supplement: props.supplementFromChange ?? null, comment });
              },
            });
          }}
        >
          Nachtrag erstellen
        </button>
      </div>
    `;

    const renderEditSupplement = () => html`
      <div class="flex flex-col gap-4">
        <p class="max-w-[600px]">
          Die Änderungen am Vertrag werden dem ausgewählten Nachtrag zugewiesen. Im Hintergrund wird ein neuer Nachtrag
          mit einer neuen Subnummer erstellt, der die Änderungen enthält. Der Basisvertrag und der ausgewählte Nachtrag
          bleiben unverändert. Auch dieser Nachtrag muss vom Arbeitnehmer unterzeichnet werden um in Kraft zu treten.
        </p>
        ${SupplementRuntime(props.supplementFromChange)} ${RenderChanges(props.supplementFromChange?.changes)}
        <button
          class="btn btn-primary"
          @click=${async () => {
            if (!props.supplementFromChange) return;
            const subNumber = getSubSupplementNumber();

            if (
              !(await confirmDialog(
                `Die unten angezeigten Änderungen werden in einem neuen Nachtrag festgehalten, welcher mit der Unternachtragsnummer ${props.supplement?.nachtragsNummer}.${subNumber} ausgestattet wird, da er einen bestehenden Nachtrag ändern würde. Möchten Sie wirklich fortfahren?`,
                { highlightDecline: true },
              ))
            )
              return;

            props.supplementFromChange.subNachtragsnummer = subNumber;
            props.supplementFromChange.nachtragsNummer = props.supplement!.nachtragsNummer;

            await guardDialog(() => props.supplementFromChange!.create(), {
              success: () => {
                void SgSnackbar.show('Unternachtrag erfolgreich übernommen.');
                props.resolve({ supplement: props.supplementFromChange ?? null, comment });
              },
            });
          }}
        >
          Unternachtrag erstellen
        </button>
      </div>
    `;

    const renderSaveSupplement = () => html`
      <div class="flex flex-col gap-4">
        <p class="max-w-[600px]">
          Änderungen werden direkt auf den ausgewählten Nachtrag geschrieben. Diese Funktion steht nur zur Verfügung,
          solange der Nachtrag vom Arbeitnehmer noch nicht unterschrieben wurde. Die Änderungen am Vertrag werden in den
          Nachtrag übernommen.
        </p>

        ${RenderChanges([...(props.supplementFromChange?.changes ?? []), ...(props.supplement?.changes ?? [])])}

        <button
          class="btn btn-primary"
          @click=${async () => {
            if (!props.supplement) return;
            if (
              !(await confirmDialog(
                `Möchten Sie wirklich die Änderungen direkt auf den Nachtrag mit der Nummer ${
                  props.supplement.nachtragsNummer
                }${props.supplement.subNachtragsnummer ? '.' + props.supplement.subNachtragsnummer : ''} schreiben?`,
              ))
            )
              return;

            // get possible changes of the contract changes and write them to the supplement. overwrite the ones on the same path and push the other ones
            // sometimes the user only changes date on the supplement. In this case this array would be empty anyway
            for (const change of props.supplementFromChange?.changes ?? []) {
              const existingChange = props.supplement.changes?.find(c => c.path === change.path);
              if (existingChange) {
                existingChange.value = change.value;
              } else {
                props.supplement.changes ??= [];
                props.supplement.changes.push(change);
              }
            }

            const changes = props.supplement.changes;
            const activeTimeFramesWithErrors: string[] = [];

            await changesContainPaths(changes, {
              generalCallback: () => {
                if (!props.supplement?.startDateGeneral) activeTimeFramesWithErrors.push('Allgemeine Informationen');
              },
              ag1Callback: () => {
                if (!props.supplement?.startDateAG1) activeTimeFramesWithErrors.push('Eingruppierung 1');
              },
              ag2Callback: () => {
                if (!props.supplement?.startDateAG2) activeTimeFramesWithErrors.push('Eingruppierung 2');
              },
            });

            if (activeTimeFramesWithErrors.length) {
              const confirmSaveWithErrors = await confirmDialog(
                'Achtung, folgende Laufzeiten sind nicht gesetzt: ' +
                  activeTimeFramesWithErrors.join(', ') +
                  '. Möchten Sie den Nachtrag trotzdem speichern?',
                { highlightDecline: true },
              );
              if (!confirmSaveWithErrors) return;
            }

            await guardDialog(() => props.supplement!.save(), {
              success: () => {
                void SgSnackbar.show('Nachtrag erfolgreich gespeichert.');
                props.resolve({ supplement: null, comment });
              },
            });
          }}
        >
          Nachtrag speichern
        </button>
      </div>
    `;

    const renderEditBase = () => html`
      <div class="flex flex-col gap-4">
        <p class="max-w-[600px]">
          Alle Änderungen welche gemacht wurden, werden statt auf einen neuen Nachtrag, direkt auf den Basisvertrag
          angewandt. Es wird weder ein Nachtrag erstellt noch geändert!
        </p>
        ${RenderChanges(props.supplementFromChange?.changes)}
        <button
          class="btn btn-primary"
          @click=${async () => {
            const contractID = props.contract?._id;
            const supplementFromChange = props.supplementFromChange;
            if (!contractID || !supplementFromChange?.changes?.length) {
              await alertDialog('Es sind keine Änderungen vorhanden, die auf den Vertrag geschrieben werden könnten.');
              return;
            }

            if (
              !(await confirmDialog(
                'Änderungen sollten eigentlich per Nachtrag festgehalten werden. Es sollten hier nur Basisdaten geändert oder Korrekturen vollzogen werden. Möchten Sie wirklich fortfahren?',
                { highlightDecline: true },
              ))
            )
              return;

            await guardDialog(
              async () => {
                await ContractAPI.saveSupplementToBase(contractID, supplementFromChange)();

                // if the user set the kuendigungsdate. we want to set it on all supplements aswell
                if (props.kuendigungSetDate) {
                  for (const supplement of props.allSupplements) {
                    supplement.automaticallyAssignedEndDateOfContract ??= props.kuendigungSetDate;
                    await supplement.save();
                  }
                }
              },
              {
                success: () => {
                  void SgSnackbar.show('Änderungen erfolgreich übernommen.');
                  props.resolve({ supplement: null, comment });
                },
              },
            );
          }}
        >
          Vertrag ändern
        </button>
      </div>
    `;

    return html`
      <div class="dialog-container items-center">
        ${
          tabs.length
            ? html`
                <div role="tablist" class="tabs tabs-border">
                  ${repeat(
                    tabs,
                    tab => tab.id,
                    tab => html`
                      <a
                        role="tab"
                        class="tab ${saveType === tab.id ? 'tab-active' : ''}"
                        @click=${() => handleTabSwitch(tab.id)}
                      >
                        ${tab.label}
                      </a>
                    `,
                  )}
                </div>
              `
            : nothing
        }
        ${
          props.simpleMode
            ? renderSimpleMode()
            : saveType === 'save_new_supplement'
              ? renderEditNewSupplement()
              : saveType === 'save_supplement_sub'
                ? renderEditSupplement()
                : saveType === 'save_supplement'
                  ? renderSaveSupplement()
                  : saveType === 'save_base_contract'
                    ? renderEditBase()
                    : // without an accessible tab there is nothing to write anywhere, so no action is offered
                      html`<p class="text-base-content/70">Keine Änderungen vorhanden</p>`
        }
      </div>
    `;
  },
);

/**
 * Opens the dialog for persisting contract changes: as new supplement, sub supplement, directly on a supplement or on the base contract.
 * Resolves with null when the user dismisses the dialog without an action.
 */
export async function showSupplementCreationDialog(
  props: SupplementCreationDialogProps,
): Promise<SupplementCreationDialogResult | null> {
  try {
    return await showDialog<SupplementCreationDialogResult>({
      content: resolve => SupplementCreationDialogContent({ ...props, resolve }),
      modalBoxClasses: 'max-w-3xl',
    });
  } catch {
    // dialog was dismissed
    return null;
  }
}
